// File: frontend/src/components/StixDisplay.tsx
import React, { useState } from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import { 
    Box, 
    CircularProgress, 
    Alert, 
    Button, 
    Paper, 
    Typography,
    Collapse,
    Tabs,
    Tab,
    useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CodeIcon from '@mui/icons-material/Code';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { alpha } from '@mui/material';
import StixVisualization from './StixVisualization';
import { cyber } from '../theme';

// Shared styling for the dark JSON code viewers
const JSON_VIEWER_BG = '#15172a';
const jsonViewerSx = {
    p: 2,
    m: 0,
    overflow: 'auto',
    bgcolor: JSON_VIEWER_BG,
    color: '#cdd2ea',
    fontFamily: cyber.fontMono,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    borderRadius: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
} as const;

interface StixDisplayProps {
    stixUrl?: string;
}

const StixDisplay: React.FC<StixDisplayProps> = ({ stixUrl }) => {
    const theme = useTheme();
    const { simpleTextFetcher } = useApi();
    const [jsonExpanded, setJsonExpanded] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    
    const { data: stixJsonString, error, isLoading } = useSWR(
        stixUrl,
        simpleTextFetcher
    );

    if (!stixUrl) {
        return (
            <Alert severity="info">
                A STIX Bundle has not been generated for this report.
            </Alert>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Could not load STIX 2.1 data. Details: {error.message}
            </Alert>
        );
    }

    if (!stixJsonString) {
        return (
            <Alert severity="warning">
                STIX 2.1 data is empty.
            </Alert>
        );
    }

    const handleDownload = () => {
        const blob = new Blob([stixJsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'stix_bundle.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Parse JSON for preview
    let prettyJson: string;
    let jsonPreview: string;
    let bundleInfo: { type?: string; id?: string; objectCount?: number } = {};
    
    try {
        const parsed = JSON.parse(stixJsonString);
        prettyJson = JSON.stringify(parsed, null, 2);
        
        // Create a preview (first 500 chars)
        jsonPreview = prettyJson.substring(0, 500) + (prettyJson.length > 500 ? '\n...' : '');
        
        // Extract bundle info
        bundleInfo = {
            type: parsed.type,
            id: parsed.id,
            objectCount: parsed.objects?.length || 0
        };
    } catch {
        prettyJson = stixJsonString;
        jsonPreview = stixJsonString.substring(0, 500) + (stixJsonString.length > 500 ? '\n...' : '');
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    return (
        <Box>
            {/* Header with actions */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button 
                    variant="contained" 
                    onClick={handleDownload} 
                    startIcon={<DownloadIcon />}
                >
                    Download STIX Bundle
                </Button>
                
                {bundleInfo.objectCount !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                        Bundle contains <Box component="span" sx={{ fontFamily: cyber.fontMono, color: alpha(cyber.accent.cyan, 0.9) }}>{bundleInfo.objectCount}</Box> objects
                    </Typography>
                )}
            </Box>

            {/* Tabs for different views */}
            <Paper sx={{ mb: 2 }}>
                <Tabs 
                    value={currentTab} 
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab 
                        icon={<AccountTreeIcon />} 
                        label="Graph View" 
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<CodeIcon />} 
                        label="JSON View" 
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {/* Graph Visualization */}
            {currentTab === 0 && (
                <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Interactive graph visualization of the STIX bundle. 
                        Drag nodes to rearrange, scroll to zoom, and click on nodes to see details.
                    </Alert>
                    <StixVisualization stixJsonString={stixJsonString} />
                </Box>
            )}

            {/* JSON View */}
            {currentTab === 1 && (
                <Box>
                    {/* Collapsible JSON Preview/Full View */}
                    <Paper 
                        sx={{ 
                            mb: 2,
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: alpha(cyber.primary, 0.08),
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: jsonExpanded ? `1px solid ${theme.palette.divider}` : 'none'
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {jsonExpanded ? 'Full STIX Bundle JSON' : 'STIX Bundle Preview'}
                            </Typography>
                            <Button
                                onClick={() => setJsonExpanded(!jsonExpanded)}
                                endIcon={jsonExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                size="small"
                            >
                                {jsonExpanded ? 'Collapse' : 'Expand Full JSON'}
                            </Button>
                        </Box>

                        {/* Preview (always visible when collapsed) */}
                        {!jsonExpanded && (
                            <Paper
                                component="pre"
                                elevation={0}
                                sx={{
                                    ...jsonViewerSx,
                                    maxHeight: '300px',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '40px',
                                        background: `linear-gradient(transparent, ${JSON_VIEWER_BG})`,
                                        pointerEvents: 'none'
                                    }
                                }}
                            >
                                <Typography 
                                    component="code" 
                                    sx={{ 
                                        fontFamily: 'inherit',
                                        fontSize: 'inherit',
                                        color: 'inherit'
                                    }}
                                >
                                    {jsonPreview}
                                </Typography>
                            </Paper>
                        )}

                        {/* Full JSON (shown when expanded) */}
                        <Collapse in={jsonExpanded}>
                            <Paper
                                component="pre"
                                elevation={0}
                                sx={{
                                    ...jsonViewerSx,
                                    maxHeight: '600px',
                                }}
                            >
                                <Typography 
                                    component="code" 
                                    sx={{ 
                                        fontFamily: 'inherit',
                                        fontSize: 'inherit',
                                        color: 'inherit'
                                    }}
                                >
                                    {prettyJson}
                                </Typography>
                            </Paper>
                        </Collapse>
                    </Paper>

                    {/* Info about the bundle */}
                    {bundleInfo.type && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Type:</strong> <Box component="span" sx={{ fontFamily: cyber.fontMono, color: alpha(cyber.accent.cyan, 0.9) }}>{bundleInfo.type}</Box> | {' '}
                                <strong>ID:</strong> <Box component="span" sx={{ fontFamily: cyber.fontMono, color: alpha(cyber.accent.cyan, 0.9), wordBreak: 'break-all' }}>{bundleInfo.id}</Box> | {' '}
                                <strong>Objects:</strong> <Box component="span" sx={{ fontFamily: cyber.fontMono, color: alpha(cyber.accent.cyan, 0.9) }}>{bundleInfo.objectCount}</Box>
                            </Typography>
                        </Alert>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default StixDisplay;